/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { ShiftCrossLocationOpen } from "@microsoft/shifts-models-data";
// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { InstrumentationService } from "sh-services";
import { AppDb } from "sh-services/data/AppDb";

import { ICrossLocationOpenShiftDatabase } from "./ICrossLocationOpenShiftDatabase";

// TODO: After fixing circular dependency, move 'CrossLocationOpenShiftDatabase' to managers package under 'crossLocationOpenShift' folder.
/**
 * The cross-location open shift database.
 */
export class CrossLocationOpenShiftDatabase implements ICrossLocationOpenShiftDatabase {
    /**
     * Instantiates a new instance of the CrossLocationOpenShiftDatabase.
     * @param database The database.
     */
    constructor(private readonly database: AppDb) {}

    /**
     * Gets the cross-location open shifts for the current user.
     * @param teamId The team id.
     * @param crossLocationTeamId The cross-location team id.
     * @returns The cross-location open shifts for the current user or undefined if no data was found.
     */
    public async getCrossLocationOpenShifts(teamId: string, crossLocationTeamId: string): Promise<ShiftCrossLocationOpen[] | undefined> {
        let openShifts: ShiftCrossLocationOpen[] | undefined = undefined;

        try {
            const records = await this.database.crossLocationOpenShifts.where({ crossLocationTeamId, teamId }).toArray();

            if (records.length > 0) {
                openShifts = records.map(({ openShift }) => openShift);
            }
        } catch (error) {
            InstrumentationService.trackException(error, "CrossLocationOpenShiftDatabase.getCrossLocationOpenShifts");
        }

        return openShifts;
    }

    /**
     * Sets the cross-location open shifts for the current user.
     * @param teamId The team id.
     * @param crossLocationTeamId The cross-location team id.
     * @param openShifts The cross-location open shifts for given team id.
     */
    public async setCrossLocationOpenShifts(teamId: string, crossLocationTeamId: string, openShifts: ShiftCrossLocationOpen[]): Promise<void> {
        try {
            const records = openShifts.map(openShift => ({
                crossLocationTeamId,
                openShift,
                teamId
            }));

            await this.database.crossLocationOpenShifts.where({ crossLocationTeamId, teamId }).delete();
            await this.database.crossLocationOpenShifts.bulkPut(records);
        } catch (error) {
            InstrumentationService.trackException(error, "CrossLocationOpenShiftDatabase.setCrossLocationOpenShifts");
        }
    }
}